import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { apiFetch } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';

interface ActiveAlert {
  rule: 'AGENT_QUIET' | 'AGENT_ERRORS' | 'RUN_DRIFT';
  severity: 'warn' | 'error' | 'fatal';
  message: string;
  firstSeen: string;
  details: Record<string, unknown>;
}

interface AlertsResp {
  active: ActiveAlert[];
  webhookConfigured: boolean;
}

const RULE_LABEL: Record<ActiveAlert['rule'], string> = {
  AGENT_QUIET: 'Agent silent',
  AGENT_ERRORS: 'Errors in log',
  RUN_DRIFT: 'Run drift',
};

const RULE_DESCRIPTION: Record<ActiveAlert['rule'], string> = {
  AGENT_QUIET:
    'No agent log events for >15 min during business hours (Mon-Sat 09:00-18:00 MYT).',
  AGENT_ERRORS:
    'One or more error/fatal events written by the agent in the last 30 minutes.',
  RUN_DRIFT:
    'A sync run finished with errors_count > 0 or records_synced ≠ records_expected (>5 record gap).',
};

export function AdminAlertsPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'alerts'],
    queryFn: () => apiFetch<AlertsResp>('/api/admin/alerts'),
    refetchInterval: 30_000,
  });

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Alerts</h1>
        <span className="main-subtitle">
          {data ? `${data.active.length} active · refreshes every 30s` : ''}
        </span>
      </header>
      <Link to="/admin" className="back-link">← Admin</Link>

      <section className="panel">
        <header className="panel-header">
          <h2 className="panel-title">How alerting works</h2>
        </header>
        <p style={{ margin: 0, color: 'var(--text-muted)', lineHeight: 1.6 }}>
          The receiver evaluates three rules every 60 seconds against{' '}
          <code>agent_log_events</code> and <code>sync_runs</code>. When a rule
          fires, the alert is logged at <code>error</code>/<code>fatal</code>{' '}
          level (visible in <Link to="/admin/sync-state">Sync state</Link>) and,
          if <code>ALERT_WEBHOOK_URL</code> is configured, POSTed as a
          Slack-compatible payload. Same alert won't re-notify within an hour.
        </p>
        <p style={{ marginTop: '0.75rem', color: 'var(--text-muted)' }}>
          Outbound webhook:{' '}
          <span className={`badge ${data?.webhookConfigured ? 'badge-ok' : 'badge-neutral'}`}>
            {data?.webhookConfigured ? 'configured' : 'not set'}
          </span>
        </p>
      </section>

      {isLoading && <div className="loading">Loading…</div>}
      {error && <div className="error">{(error as Error).message}</div>}

      {data && (
        <>
          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Active alerts</h2>
              <span className="panel-subtitle">{data.active.length}</span>
            </header>
            {data.active.length === 0 ? (
              <div className="empty">
                Nothing firing. The agent is talking, error rates are clean,
                and the most recent sync runs are within tolerance.
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Rule</th>
                    <th>Severity</th>
                    <th>Message</th>
                    <th>First seen</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {data.active.map((a, i) => (
                    <tr key={`${a.rule}-${i}`}>
                      <td className="mono">{RULE_LABEL[a.rule]}</td>
                      <td>
                        <span className={`badge ${severityBadge(a.severity)}`}>
                          {a.severity}
                        </span>
                      </td>
                      <td>{a.message}</td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {dateTime(a.firstSeen)}
                      </td>
                      <td className="mono" style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                        {Object.keys(a.details).length === 0 ? '—' : JSON.stringify(a.details)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Rule reference</h2>
            </header>
            <table className="table">
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>What it checks</th>
                </tr>
              </thead>
              <tbody>
                {(Object.keys(RULE_LABEL) as ActiveAlert['rule'][]).map((r) => (
                  <tr key={r}>
                    <td className="mono">{r}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{RULE_DESCRIPTION[r]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </>
  );
}

function severityBadge(s: ActiveAlert['severity']): string {
  if (s === 'fatal' || s === 'error') return 'badge-cancel';
  if (s === 'warn') return 'badge-pending';
  return 'badge-neutral';
}
